import { useState, useEffect } from 'react'
import { NavLink, Link } from 'react-router-dom'
import BrandMark from './BrandMark.jsx'

/**
 * MarketingHeader: sticky top bar for the legal and trust pages.
 *
 * PRE-LAUNCH (2026-09-21). claimguru.com serves one product page, the coming
 * soon page at "/". The product-marketing URLs (/benefits, /pricing, /about)
 * are retired and 301 to "/" at the edge, so this nav does not link to them,
 * and there is no free-trial CTA: a trial is a claim the product cannot be
 * held to before launch. Restore both together when the marketing routes come
 * back in src/App.jsx.
 *
 * Only the pages that stay reachable are listed here. A legal page is never
 * removed from this site.
 */
const NAV = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
  { to: '/security', label: 'Security' },
]

export default function MarketingHeader() {
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      className="sticky top-0 z-50 w-full transition-all"
      style={{
        backdropFilter: scrolled ? 'saturate(140%) blur(14px)' : 'saturate(120%) blur(8px)',
        background: scrolled
          ? 'color-mix(in oklab, var(--v3-canvas) 78%, transparent)'
          : 'color-mix(in oklab, var(--v3-canvas) 55%, transparent)',
        borderBottom: scrolled ? '1px solid var(--v3-border-subtle)' : '1px solid transparent',
        transitionDuration: 'var(--v3-duration-base)',
        transitionTimingFunction: 'var(--v3-ease)',
      }}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link
          to="/"
          className="flex items-center gap-2.5 group"
          aria-label="ClaimGuru home"
        >
          <BrandMark className="h-7 w-7" />
          <span
            className="font-semibold tracking-tight text-[15px]"
            style={{ color: 'var(--v3-text-primary)', fontFamily: 'var(--v3-font-sans)' }}
          >
            ClaimGuru
          </span>
        </Link>

        <nav className="flex items-center gap-1" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `px-2.5 sm:px-3.5 py-2 text-[13px] sm:text-sm rounded-md transition-colors ${
                  isActive ? 'nav-active' : 'nav-default'
                }`
              }
              style={({ isActive }) => ({
                color: isActive ? 'var(--v3-text-primary)' : 'var(--v3-text-muted)',
                background: isActive ? 'var(--v3-elevated)' : 'transparent',
                fontFamily: 'var(--v3-font-sans)',
              })}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  )
}
